import { assertKnownPageSignalPatchRoots } from "../js/page-signal-patch-guard.js";

export const MAP_PAGE_PERSIST_SIGNAL_FILTER =
  /^_(?:map_ui\.(?:windowUi|layers(?:\.|$)|search\.expression)|map_bridged\.ui\.(?:showPoints|showPointIcons|viewMode|pointIconScale)|map_bridged\.filters\.(?:layerIdsVisible|layerIdsOrdered|layerFilterBindingIdsDisabledByLayer|layerOpacities|layerHighlights|layerConfidenceShowcase|layerClipMasks|layerWaypointConnectionsVisible|layerWaypointLabelsVisible|layerPointIconsVisible|layerPointIconScales|layerPointClusteringEnabled|tradeNpcClassesVisible|filterOnlyActiveConditionGroups|lootConditionSelection)|map_bookmarks\.entries)(?:\.|$)/;

export const MAP_PAGE_EXACT_PATCH_PATHS = Object.freeze([
  "_map_ui.layers.expandedLayerIds",
  "_map_ui.layers.hoverFactsVisibleByLayer",
  "_map_ui.layers.sampleHoverVisibleByLayer",
  "_map_ui.layers.lineagePanelVisibleByLayer",
  "_map_ui.search.expression",
  "_map_ui.search.rawExpressionTree",
  "_map_ui.search.expressionIssues",
  "_map_bridged.filters.layerFilterBindingIdsDisabledByLayer",
  "_map_bridged.filters.layerOpacities",
  "_map_bridged.filters.layerHighlights",
  "_map_bridged.filters.layerConfidenceShowcase",
  "_map_bridged.filters.layerClipMasks",
  "_map_bridged.filters.layerWaypointConnectionsVisible",
  "_map_bridged.filters.layerWaypointLabelsVisible",
  "_map_bridged.filters.layerPointIconsVisible",
  "_map_bridged.filters.layerPointIconScales",
  "_map_bridged.filters.layerPointClusteringEnabled",
  "_map_bridged.filters.tradeNpcClassesVisible",
  "_map_bridged.filters.lootConditionSelection",
  "_map_bridged.filters.lootConditionContext",
  "_map_runtime.view",
  "_map_runtime.selection",
  "_map_runtime.catalog",
  "_map_runtime.statuses",
  "_map_session.view",
  "_map_session.selection",
]);

function cloneJson(value) {
  return JSON.parse(JSON.stringify(value));
}

function isPlainObject(value) {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function mergeObjectPatch(target, patch, exactPatchPaths, prefix = "") {
  if (!isPlainObject(target) || !isPlainObject(patch)) {
    return target;
  }
  for (const [key, value] of Object.entries(patch)) {
    const path = prefix ? `${prefix}.${key}` : key;
    if (exactPatchPaths.has(path)) {
      target[key] = value;
      continue;
    }
    if (Array.isArray(value)) {
      target[key] = value;
      continue;
    }
    if (isPlainObject(value)) {
      const currentTarget = target[key];
      const nextTarget = isPlainObject(currentTarget) ? currentTarget : {};
      if (nextTarget !== currentTarget) {
        target[key] = nextTarget;
      }
      mergeObjectPatch(nextTarget, value, exactPatchPaths, path);
      continue;
    }
    target[key] = value;
  }
  return target;
}

function exactReplacementMergePatch(current, next) {
  if (!isPlainObject(current) || !isPlainObject(next)) {
    return cloneJson(next);
  }
  const difference = cloneJson(next);
  for (const key of new Set([...Object.keys(current), ...Object.keys(next)])) {
    if (!Object.prototype.hasOwnProperty.call(next, key)) {
      difference[key] = null;
      continue;
    }
    if (isPlainObject(current[key]) && isPlainObject(next[key])) {
      difference[key] = exactReplacementMergePatch(current[key], next[key]);
    }
  }
  return difference;
}

function addExactReplacementTombstones(
  current,
  patch,
  exactPatchPaths,
  prefix = "",
) {
  for (const [key, value] of Object.entries(patch)) {
    const path = prefix ? `${prefix}.${key}` : key;
    if (exactPatchPaths.has(path)) {
      patch[key] = exactReplacementMergePatch(current?.[key], value);
      continue;
    }
    if (isPlainObject(value)) {
      addExactReplacementTombstones(
        isPlainObject(current?.[key]) ? current[key] : {},
        value,
        exactPatchPaths,
        path,
      );
    }
  }
  return patch;
}

export function applyMapPageSignalsPatch(signals, patch, options = {}) {
  if (!isPlainObject(signals) || !isPlainObject(patch)) {
    return;
  }
  assertKnownPageSignalPatchRoots("map", signals, patch);
  const exactPatchPaths = new Set(
    options.exactPatchPaths ?? MAP_PAGE_EXACT_PATCH_PATHS,
  );
  mergeObjectPatch(signals, cloneJson(patch), exactPatchPaths);
}

export function datastarMergePatchForMapPagePatch(signals, patch, options = {}) {
  if (!isPlainObject(signals) || !isPlainObject(patch)) {
    throw new TypeError("Map Datastar patches require record-shaped state and patch");
  }
  assertKnownPageSignalPatchRoots("map", signals, patch);
  const exactPatchPaths = new Set(
    options.exactPatchPaths ?? MAP_PAGE_EXACT_PATCH_PATHS,
  );
  return addExactReplacementTombstones(
    signals,
    cloneJson(patch),
    exactPatchPaths,
  );
}

export function patchMatchesSignalFilter(patch, filter, prefix = "") {
  if (!isPlainObject(patch)) {
    return false;
  }
  const include = filter?.include && typeof filter.include.test === "function"
    ? filter.include
    : null;
  const exclude = filter?.exclude && typeof filter.exclude.test === "function"
    ? filter.exclude
    : null;
  return Object.entries(patch).some(([key, value]) => {
    const path = prefix ? `${prefix}.${key}` : key;
    if (include) {
      if (include.test(path)) {
        return true;
      }
    } else if (exclude) {
      if (!exclude.test(path)) {
        return true;
      }
    }
    return isPlainObject(value) && patchMatchesSignalFilter(value, filter, path);
  });
}

export function patchMatchesMapPagePersistFilter(patch) {
  return patchMatchesSignalFilter(patch, { include: MAP_PAGE_PERSIST_SIGNAL_FILTER });
}
